import { useState, useEffect, useMemo } from "react";
import { resolveImg, thumbImg, SHOP_KINDS, kindLabel, fromPrice, hasSizes } from "./data";
import { useCurrency, useMoney } from "./lib/currency";
import { pathFor } from "./route";
import { Ic, P, Reveal, ArtCard, ShopCard, SectionHead, Lightbox } from "./ui";
import { shopToken, findVariant } from "./lib/cart";

/* A real <a href> that still navigates without a page reload. */
export function Link({ page, id, go, className, children, ...rest }) {
  return (
    <a href={pathFor(page, id)} className={className} {...rest}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        go(page, id);
      }}>{children}</a>
  );
}

/* ============================ HOME ============================ */
export function Home({ paintings, testimonials, settings, go, wishlist, toggleWish, subscribe }) {
  const featured = paintings.filter((p) => p.featured);
  const pool = featured.length ? featured : paintings;
  const hero = pool[0];
  const customHero = settings.hero_image || "";
  const selected = pool.length > 1 ? pool.slice(1, 7) : paintings.slice(0, 6);
  const collections = useMemo(() => {
    const cats = [...new Set(paintings.map((p) => p.category).filter(Boolean))].slice(0, 2);
    return cats.map((c) => {
      const inCat = paintings.filter((p) => p.category === c);
      return { name: c, count: inCat.length, cover: inCat[inCat.length - 1] };
    });
  }, [paintings]);
  const [email, setEmail] = useState("");

  return (
    <div className="kg-page">
      <section className="kg-wrap kg-hero">
        <div className="kg-hero-copy">
          <div className="kg-caps kg-hero-eyebrow">Original oil paintings · Karima El Ghobri</div>
          <h1 className="kg-display kg-hero-title">Light, water, and <em>the quiet in between.</em></h1>
          <p className="kg-lede">Each painting here is an original, built by hand in thin layers of oil and signed in my
            studio. When one finds a home, it is yours alone.</p>
          <div className="kg-hero-cta">
            <Link page="gallery" go={go} className="kg-btn kg-btn-primary">Explore the gallery <Ic d={P.arrow} size={16} /></Link>
            <Link page="contact" go={go} className="kg-btn kg-btn-ghost">Commissions &amp; inquiries</Link>
          </div>
          <ul className="kg-facts">
            <li>Painted by hand</li><li>Signed originals</li><li>One of a kind</li>
          </ul>
        </div>

        {(customHero || hero) && (
          <div className="kg-hero-art">
            {customHero ? (
              <div className="kg-hero-main">
                <img src={customHero} alt="Artwork by Karima El Ghobri" fetchpriority="high" />
              </div>
            ) : (
              <Link page="painting" id={hero.id} go={go} className="kg-hero-main" aria-label={`View ${hero.title}`}>
                <img src={resolveImg(hero.images[0])} alt={`${hero.title} — ${hero.medium}, ${hero.dims}`} fetchpriority="high" />
              </Link>
            )}
          </div>
        )}
      </section>

      <section className="kg-wrap kg-section">
        <Reveal>
          <SectionHead eyebrow="Currently on the wall" title="Selected works" action="View all paintings" onAction={() => go("gallery")} />
        </Reveal>
        <div className="kg-grid">
          {selected.map((a, i) => (
            <Reveal key={a.id} delay={(i % 3) * 70}>
              <ArtCard art={a} onOpen={(id) => go("painting", id)} wished={wishlist.includes(a.id)} onWish={toggleWish} />
            </Reveal>
          ))}
        </div>
      </section>

      {collections.length > 0 && (
        <section className="kg-wrap kg-section kg-tight">
          <Reveal><SectionHead eyebrow="Collections" title="Browse by subject" /></Reveal>
          <div className="kg-collections">
            {collections.map((c, i) => (
              <Reveal key={c.name} delay={i * 90}>
                <a className="kg-collection" href={pathFor("gallery")}
                  onClick={(e) => { e.preventDefault(); go("gallery", null, { cat: c.name }); }}>
                  {c.cover && <img src={resolveImg(c.cover.images[0])} alt="" loading="lazy" />}
                  <span className="kg-collection-shade" aria-hidden="true" />
                  <span className="kg-collection-label">
                    <span className="kg-display kg-collection-name">{c.name}</span>
                    <span>{c.count} {c.count === 1 ? "painting" : "paintings"} <Ic d={P.arrow} size={15} /></span>
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="kg-wrap kg-section kg-statement">
        <Reveal>
          <div className="kg-caps">From the studio</div>
          <p className="kg-display kg-quote">“{settings.statement}”</p>
          <Link page="about" go={go} className="kg-textlink">About the artist <Ic d={P.arrow} size={16} /></Link>
        </Reveal>
      </section>

      {testimonials.length > 0 && (
        <section className="kg-band">
          <div className="kg-wrap kg-section">
            <SectionHead eyebrow="Collectors' words" title="What people say" />
            <div className="kg-quotes">
              {testimonials.map((t) => (
                <figure key={t.id} className="kg-quotecard">
                  <blockquote className="kg-display">“{t.text}”</blockquote>
                  <figcaption className="kg-caps">{t.name}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="kg-wrap kg-section kg-tight">
        <Reveal className="kg-news">
          <div>
            <div className="kg-caps">Studio letter</div>
            <h2 className="kg-display kg-h2">New paintings, first.</h2>
            <p className="kg-lede kg-news-p">Hear when new originals are released — before they reach the gallery wall.</p>
          </div>
          <form className="kg-news-form" onSubmit={(e) => { e.preventDefault(); if (email.trim()) { subscribe(email.trim()); setEmail(""); } }}>
            <input className="kg-input" type="email" required placeholder="you@example.com" value={email}
              aria-label="Email address for newsletter" onChange={(e) => setEmail(e.target.value)} />
            <button className="kg-btn kg-btn-primary" type="submit">Subscribe</button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}

/* ============================ GALLERY ============================ */
export function Gallery({ paintings, go, wishlist, toggleWish, initialCat = "all" }) {
  const [q, setQ] = useState("");
  const [avail, setAvail] = useState("all");
  const [cat, setCat] = useState(initialCat);
  const [sort, setSort] = useState("arranged");
  useEffect(() => { setCat(initialCat); }, [initialCat]);
  const cats = useMemo(() => [...new Set(paintings.map((p) => p.category).filter(Boolean))], [paintings]);
  const shown = useMemo(() => {
    let list = paintings.filter((p) =>
      (avail === "all" || p.status === avail) &&
      (cat === "all" || p.category === cat) &&
      (q.trim() === "" || (p.title + " " + (p.description || "")).toLowerCase().includes(q.toLowerCase())));
    if (sort === "lo") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "hi") list = [...list].sort((a, b) => b.price - a.price);
    if (sort === "new") list = [...list].sort((a, b) => String(b.year).localeCompare(String(a.year)));
    return list;
  }, [paintings, q, avail, cat, sort]);

  return (
    <div className="kg-page kg-wrap kg-section">
      <header className="kg-pagehead">
        <div className="kg-caps">The gallery</div>
        <h1 className="kg-display kg-h1">Original paintings</h1>
        <p className="kg-lede">Every work is a one-of-a-kind original, signed and ready to hang.</p>
      </header>

      <div className="kg-toolbar">
        {cats.length > 1 && (
          <div className="kg-chips" role="group" aria-label="Filter by collection">
            {["all", ...cats].map((c) => (
              <button key={c} className={"kg-chip" + (cat === c ? " on" : "")} aria-pressed={cat === c} onClick={() => setCat(c)}>
                {c === "all" ? "All works" : c}
              </button>
            ))}
          </div>
        )}
        <div className="kg-tools">
          <div className="kg-search">
            <Ic d={P.search} size={16} />
            <input className="kg-input" placeholder="Search" value={q} aria-label="Search paintings" onChange={(e) => setQ(e.target.value)} />
          </div>
          <select className="kg-select" value={avail} aria-label="Filter by availability" onChange={(e) => setAvail(e.target.value)}>
            <option value="all">Any status</option><option value="available">Available</option><option value="sold">Sold</option>
          </select>
          <select className="kg-select" value={sort} aria-label="Sort paintings" onChange={(e) => setSort(e.target.value)}>
            <option value="arranged">Featured order</option><option value="new">Newest</option><option value="lo">Price: low to high</option><option value="hi">Price: high to low</option>
          </select>
        </div>
      </div>

      <p className="kg-count" aria-live="polite">{shown.length} {shown.length === 1 ? "painting" : "paintings"}</p>
      {shown.length === 0
        ? <p className="kg-muted">No paintings match that search yet. Clear a filter to see the full wall.</p>
        : <div className="kg-grid">
            {shown.map((a, i) => (
              <ArtCard key={a.id} art={a} eager={i < 3} onOpen={(id) => go("painting", id)}
                wished={wishlist.includes(a.id)} onWish={toggleWish} />
            ))}
          </div>}
    </div>
  );
}

/* ============================ DETAIL ============================ */
export function Detail({ art, paintings, go, cart, addCart, wishlist, toggleWish, notify }) {
  const money = useMoney();
  const [imgIdx, setImgIdx] = useState(0);
  const [light, setLight] = useState(false);
  useEffect(() => { setImgIdx(0); }, [art?.id]);
  if (!art) {
    return (
      <div className="kg-wrap kg-section kg-page">
        <p className="kg-muted">This painting is no longer in the gallery.</p>
        <Link page="gallery" go={go} className="kg-btn kg-btn-ghost">Back to the gallery</Link>
      </div>
    );
  }
  const related = paintings.filter((p) => p.id !== art.id)
    .sort((a, b) => (b.category === art.category) - (a.category === art.category)).slice(0, 3);
  const inCart = cart.includes(art.id);
  const wished = wishlist.includes(art.id);
  const sold = art.status === "sold";
  const share = async () => {
    const text = `“${art.title}” — an original oil painting by Karima El Ghobri (${art.dims}, ${art.year})`;
    try {
      if (navigator.share) { await navigator.share({ title: art.title, text, url: window.location.href }); }
      else { await navigator.clipboard.writeText(`${text} ${window.location.href}`); notify("Link copied — paste anywhere to share"); }
    } catch { /* cancelled */ }
  };

  return (
    <div className="kg-page kg-wrap">
      <nav className="kg-crumbs" aria-label="Breadcrumb">
        <Link page="gallery" go={go}><Ic d={P.back} size={15} /> Gallery</Link>
        {art.category && <span aria-hidden="true">/</span>}
        {art.category && <span>{art.category}</span>}
      </nav>

      <div className="kg-detail">
        <div className="kg-detail-media">
          <button className="kg-viewer" onClick={() => setLight(true)} aria-label={`Open ${art.title} full screen`}>
            <img src={resolveImg(art.images[imgIdx])} alt={`${art.title} — ${art.medium}, ${art.dims}`} />
            <span className="kg-zoom"><Ic d={P.zoom} size={16} /> Zoom</span>
          </button>
          {art.images.length > 1 && (
            <div className="kg-thumbs">
              {art.images.map((im, i) => (
                <button key={i} className={i === imgIdx ? "on" : ""} onClick={() => setImgIdx(i)} aria-label={`Show view ${i + 1}`}>
                  <img src={thumbImg(im)} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="kg-detail-info">
          <div className="kg-caps">{art.category || "Original work"}</div>
          <h1 className="kg-display kg-h1 kg-detail-title">{art.title}</h1>
          <div className={"kg-detail-price" + (sold ? " sold" : "")}>
            {sold ? <><span className="kg-dot" aria-hidden="true" /> Sold — now in a collector's home</> : money(art.price)}
          </div>
          {art.description && <p className="kg-lede kg-detail-desc">{art.description}</p>}

          <div className="kg-detail-actions">
            {!sold && (
              <button className="kg-btn kg-btn-primary kg-btn-lg" disabled={inCart} onClick={() => addCart(art.id)}>
                <Ic d={P.bag} size={17} /> {inCart ? "In your cart" : "Add to cart"}
              </button>
            )}
            <button className="kg-btn kg-btn-ghost kg-btn-lg" onClick={() => toggleWish(art.id)} aria-pressed={wished}>
              <Ic d={P.heart} size={17} fill={wished ? "currentColor" : "none"} /> {wished ? "Saved" : "Save"}
            </button>
            <button className="kg-btn kg-btn-ghost kg-btn-lg" onClick={share}><Ic d={P.share} size={17} /> Share</button>
          </div>

          <dl className="kg-specs">
            <div><dt>Medium</dt><dd>{art.medium}</dd></div>
            <div><dt>Size</dt><dd>{art.dims}</dd></div>
            <div><dt>Year</dt><dd>{art.year}</dd></div>
            <div><dt>Edition</dt><dd>Original, one of a kind</dd></div>
          </dl>

          {!sold && (
            <p className="kg-note">Prefer to talk first?{" "}
              <Link page="contact" go={go} className="kg-inlinelink">Send me a note</Link> about this painting.</p>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <section className="kg-section kg-tight">
          <SectionHead eyebrow="Hanging nearby" title="You may also love" />
          <div className="kg-grid">
            {related.map((a) => (
              <ArtCard key={a.id} art={a} onOpen={(id) => go("painting", id)} wished={wishlist.includes(a.id)} onWish={toggleWish} />
            ))}
          </div>
        </section>
      )}
      {light && <Lightbox src={resolveImg(art.images[imgIdx])} alt={art.title} onClose={() => setLight(false)} />}
    </div>
  );
}

/* ============================ ABOUT ============================ */
/* ============================ PRINTS ============================ */
/* The shop: prints, stickers and bookmarks, one tab each.

   Built to read as a sibling of the gallery rather than a different shop — same
   page head, same chips, same grid. The chips replace the gallery's collection
   filter: where that one filters paintings by subject, this one switches
   between three kinds of thing. */
export function Prints({ shopItems, go, initialKind }) {
  const [kind, setKind] = useState(initialKind || SHOP_KINDS[0].kind);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("arranged");
  useEffect(() => { if (initialKind) setKind(initialKind); }, [initialKind]);

  /* Only items the artist has ticked as visible, and only ones with a price —
     a half-finished listing should never show a buyer "—" where a price goes. */
  const live = useMemo(
    () => (shopItems || []).filter((i) => i.active !== false && fromPrice(i) > 0),
    [shopItems]);

  const counts = useMemo(() => {
    const out = {};
    for (const k of SHOP_KINDS) out[k.kind] = live.filter((i) => i.kind === k.kind).length;
    return out;
  }, [live]);

  const shown = useMemo(() => {
    let list = live.filter((i) =>
      i.kind === kind &&
      (q.trim() === "" || (i.title + " " + (i.description || "")).toLowerCase().includes(q.toLowerCase())));
    if (sort === "lo") list = [...list].sort((a, b) => fromPrice(a) - fromPrice(b));
    if (sort === "hi") list = [...list].sort((a, b) => fromPrice(b) - fromPrice(a));
    return list;
  }, [live, kind, q, sort]);

  const active = SHOP_KINDS.find((k) => k.kind === kind) || SHOP_KINDS[0];
  const noun = shown.length === 1 ? active.one : active.label.toLowerCase();

  return (
    <div className="kg-page kg-wrap kg-section">
      <header className="kg-pagehead">
        <div className="kg-caps">The print shop</div>
        <h1 className="kg-display kg-h1">Prints &amp; small things</h1>
        <p className="kg-lede">{active.blurb}</p>
      </header>

      <div className="kg-toolbar">
        <div className="kg-chips" role="group" aria-label="Choose what to browse">
          {SHOP_KINDS.map((k) => (
            <button key={k.kind} className={"kg-chip" + (kind === k.kind ? " on" : "")}
              aria-pressed={kind === k.kind} onClick={() => setKind(k.kind)}>
              {k.label}
            </button>
          ))}
        </div>
        <div className="kg-tools">
          <div className="kg-search">
            <Ic d={P.search} size={16} />
            <input className="kg-input" placeholder="Search" value={q}
              aria-label="Search the print shop" onChange={(e) => setQ(e.target.value)} />
          </div>
          <select className="kg-select" value={sort} aria-label="Sort" onChange={(e) => setSort(e.target.value)}>
            <option value="arranged">Featured order</option>
            <option value="lo">Price: low to high</option>
            <option value="hi">Price: high to low</option>
          </select>
        </div>
      </div>

      <p className="kg-count" aria-live="polite">{shown.length} {noun}</p>

      {shown.length === 0
        ? <p className="kg-muted">
            {counts[kind] === 0
              ? `No ${active.label.toLowerCase()} in the shop just yet — they're on their way.`
              : "Nothing matches that search. Clear it to see everything."}
          </p>
        : <div className="kg-grid">
            {shown.map((item, i) => (
              <ShopCard key={item.id} item={item} eager={i < 3} onOpen={(id) => go("shopitem", id)} />
            ))}
          </div>}

    </div>
  );
}

/* One print / sticker / bookmark, with its sizes. */
export function ShopItemPage({ item, shopItems, go, cart, addShop, notify }) {
  const money = useMoney();
  const [imgIdx, setImgIdx] = useState(0);
  const [light, setLight] = useState(false);
  const [variantId, setVariantId] = useState(null);
  useEffect(() => { setImgIdx(0); setVariantId(null); }, [item?.id]);

  if (!item || item.active === false) {
    return (
      <div className="kg-wrap kg-section kg-page">
        <p className="kg-muted">This isn't in the shop any more.</p>
        <Link page="prints" go={go} className="kg-btn kg-btn-ghost">Back to the print shop</Link>
      </div>
    );
  }

  const variants = (item.variants || []).filter((v) => Number(v.price) > 0);
  /* Default to the cheapest size rather than whichever was entered first:
     the lowest price is what the card promised on the way in. */
  const chosen = findVariant(item, variantId)
    || [...variants].sort((a, b) => Number(a.price) - Number(b.price))[0]
    || null;
  const inCart = chosen ? cart.includes(shopToken(item.id, chosen.id)) : false;
  const qtyInCart = chosen ? cart.filter((t) => t === shopToken(item.id, chosen.id)).length : 0;

  const related = (shopItems || [])
    .filter((x) => x.id !== item.id && x.active !== false && fromPrice(x) > 0)
    .sort((a, b) => (b.kind === item.kind) - (a.kind === item.kind))
    .slice(0, 3);

  const share = async () => {
    const text = `“${item.title}” by Karima El Ghobri`;
    try {
      if (navigator.share) { await navigator.share({ title: item.title, text, url: window.location.href }); }
      else { await navigator.clipboard.writeText(`${text} ${window.location.href}`); notify("Link copied — paste anywhere to share"); }
    } catch { /* cancelled */ }
  };

  return (
    <div className="kg-page kg-wrap">
      <nav className="kg-crumbs" aria-label="Breadcrumb">
        <Link page="prints" go={go}><Ic d={P.back} size={15} /> Print shop</Link>
        <span aria-hidden="true">/</span>
        <span>{kindLabel(item.kind)}</span>
      </nav>

      <div className="kg-detail">
        <div className="kg-detail-media">
          <button className="kg-viewer" onClick={() => setLight(true)} aria-label={`Open ${item.title} full screen`}>
            <img src={resolveImg((item.images || [])[imgIdx])} alt={item.title} />
            <span className="kg-zoom"><Ic d={P.zoom} size={16} /> Zoom</span>
          </button>
          {(item.images || []).length > 1 && (
            <div className="kg-thumbs">
              {item.images.map((im, i) => (
                <button key={i} className={i === imgIdx ? "on" : ""} onClick={() => setImgIdx(i)} aria-label={`Show view ${i + 1}`}>
                  <img src={thumbImg(im)} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="kg-detail-info">
          <div className="kg-caps">{kindLabel(item.kind)}</div>
          <h1 className="kg-display kg-h1 kg-detail-title">{item.title}</h1>
          {/* A print only has a size picker when there is a choice. With a single
              size there was nothing to pick AND nothing to read, so the one fact
              a buyer most wants — how big is it — appeared nowhere on the page.
              It sits here instead, the way a painting states its dimensions. */}
          {hasSizes(item.kind) && variants.length === 1 && chosen && chosen.label && (
            <div className="kg-detail-meta">{chosen.label}</div>
          )}
          <div className="kg-detail-price">{chosen ? money(chosen.price) : "—"}</div>
          {item.description && <p className="kg-lede kg-detail-desc">{item.description}</p>}

          {/* Stickers and bookmarks are one thing at one price — no picker. */}
          {hasSizes(item.kind) && variants.length > 1 && (
            <div className="kg-field kg-sizes">
              <label id="size-label">Size</label>
              <div className="kg-chips" role="group" aria-labelledby="size-label">
                {variants.map((v) => (
                  <button key={v.id} className={"kg-chip" + (chosen && chosen.id === v.id ? " on" : "")}
                    aria-pressed={chosen && chosen.id === v.id}
                    onClick={() => setVariantId(v.id)}>
                    {v.label} · {money(v.price)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="kg-detail-actions">
            <button className="kg-btn kg-btn-primary kg-btn-lg" disabled={!chosen}
              onClick={() => chosen && addShop(item, chosen)}>
              <Ic d={P.bag} size={17} /> {inCart ? `Add another (${qtyInCart} in cart)` : "Add to cart"}
            </button>
            <button className="kg-btn kg-btn-ghost kg-btn-lg" onClick={share}><Ic d={P.share} size={17} /> Share</button>
          </div>

          {inCart && (
            <p className="kg-small kg-muted">
              <Link page="cart" go={go} className="kg-inlinelink">Go to your cart</Link> when you're ready.
            </p>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <section className="kg-section">
          <SectionHead eyebrow="More from the shop" title="You might also like" />
          <div className="kg-grid">
            {related.map((x) => <ShopCard key={x.id} item={x} onOpen={(id) => go("shopitem", id)} />)}
          </div>
        </section>
      )}

      {light && <Lightbox src={resolveImg((item.images || [])[imgIdx])} alt={item.title} onClose={() => setLight(false)} />}
    </div>
  );
}

export function About({ paintings, settings, go }) {
  const side = paintings.find((p) => p.featured) || paintings[0];
  return (
    <div className="kg-page kg-wrap kg-section">
      <div className="kg-about">
        <div className="kg-about-copy">
          <div className="kg-caps">About the artist</div>
          <h1 className="kg-display kg-h1">Karima El Ghobri</h1>
          <p className="kg-lede kg-about-first">I'm Karima, an oil painter. My work lives in the quiet spaces — the pause in a glance,
            the last minutes of light over water, the stories carried in a face.</p>
          <p>I paint in oils because they are patient the way I try to be: each painting is built in thin, deliberate
            layers over days and weeks, and every one that leaves my studio is an original, made once and never repeated.</p>
          <p>Painting, for me, has never been a private act. I share the whole journey — the underpaintings, the
            mistakes, the small victories — on Instagram and YouTube, where a community of fellow painters and
            collectors follows each canvas from blank linen to finished work. If you've found your way here from one
            of those videos: welcome. This gallery is where those paintings find their homes.</p>
          <p>When you collect one of my pieces, you're not just buying an image — you're taking home hours of slow,
            honest work, and a little of the light I was chasing that week. Thank you for being here.</p>
          <div className="kg-about-cta">
            <a className="kg-btn kg-btn-ghost" href={settings.instagram} target="_blank" rel="noreferrer"><Ic d={P.ig} size={16} /> Instagram</a>
            <a className="kg-btn kg-btn-ghost" href={settings.youtube} target="_blank" rel="noreferrer"><Ic d={P.yt} size={16} /> YouTube</a>
            <Link page="gallery" go={go} className="kg-btn kg-btn-primary">See the paintings</Link>
          </div>
        </div>
        {settings.about_image ? (
          <div className="kg-about-art">
            <img src={settings.about_image} alt="Karima El Ghobri" loading="lazy" />
          </div>
        ) : side && (
          <Link page="painting" id={side.id} go={go} className="kg-about-art" aria-label={`View ${side.title}`}>
            <img src={resolveImg(side.images[0])} alt={`${side.title} by Karima El Ghobri`} loading="lazy" />
          </Link>
        )}
      </div>
    </div>
  );
}

/* ============================ CONTACT ============================ */
export function Contact({ settings, sendMessage }) {
  const [f, setF] = useState({ name: "", email: "", topic: "A question", message: "" });
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="kg-page kg-wrap kg-section">
      <div className="kg-contact">
        <div>
          <div className="kg-caps">Get in touch</div>
          <h1 className="kg-display kg-h1">Say hello.</h1>
          <p className="kg-lede">Questions about a painting, commission ideas, collaborations — I read every message myself
            and love hearing from you.</p>
          <ul className="kg-contact-list">
            <li><Ic d={P.mail} size={18} /><a href={"mailto:" + settings.email}>{settings.email}</a></li>
            <li><Ic d={P.ig} size={18} /><a href={settings.instagram} target="_blank" rel="noreferrer">Instagram</a></li>
            <li><Ic d={P.yt} size={18} /><a href={settings.youtube} target="_blank" rel="noreferrer">YouTube</a></li>
          </ul>
        </div>
        <div className="kg-card-form">
          {sent ? (
            <div>
              <div className="kg-okline"><Ic d={P.check} size={18} /> Message sent</div>
              <p className="kg-muted">Thank you, {f.name.split(" ")[0] || "friend"} — your note is in my studio inbox and I'll reply to {f.email} soon.</p>
            </div>
          ) : (
            <>
              <div className="kg-field"><label htmlFor="c-name">Your name</label><input id="c-name" className="kg-input" value={f.name} onChange={set("name")} autoComplete="name" /></div>
              <div className="kg-field"><label htmlFor="c-email">Email</label><input id="c-email" className="kg-input" type="email" value={f.email} onChange={set("email")} autoComplete="email" /></div>
              <div className="kg-field"><label htmlFor="c-topic">Topic</label>
                <select id="c-topic" className="kg-select" value={f.topic} onChange={set("topic")}>
                  <option>A question</option><option>Commission request</option><option>Collaboration</option><option>Something else</option>
                </select></div>
              <div className="kg-field"><label htmlFor="c-msg">Message</label><textarea id="c-msg" className="kg-textarea" rows={5} value={f.message} onChange={set("message")} /></div>
              <button className="kg-btn kg-btn-primary kg-btn-lg" disabled={!f.name.trim() || !f.email.trim() || !f.message.trim()}
                onClick={() => { sendMessage(f); setSent(true); }}>Send message</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================ WISHLIST ============================ */
export function Wishlist({ paintings, wishlist, go, toggleWish }) {
  const list = paintings.filter((p) => wishlist.includes(p.id));
  return (
    <div className="kg-page kg-wrap kg-section">
      <header className="kg-pagehead">
        <div className="kg-caps">Saved for later</div>
        <h1 className="kg-display kg-h1">Your wishlist</h1>
      </header>
      {list.length === 0
        ? (<div><p className="kg-muted">Nothing saved yet. Tap the heart on any painting to keep it here while you decide.</p>
            <Link page="gallery" go={go} className="kg-btn kg-btn-primary">Browse the gallery</Link></div>)
        : <div className="kg-grid">{list.map((a) => <ArtCard key={a.id} art={a} onOpen={(id) => go("painting", id)} wished onWish={toggleWish} />)}</div>}
    </div>
  );
}

/* ============================ CART ============================ */
export function CartPage({ lines, total, removeLine, addLine, go }) {
  const { money, base, approx } = useCurrency();
  return (
    <div className="kg-page kg-wrap kg-section kg-narrow">
      <header className="kg-pagehead">
        <div className="kg-caps">Your collection in progress</div>
        <h1 className="kg-display kg-h1">Cart</h1>
      </header>
      {lines.length === 0 ? (
        <div><p className="kg-muted">Your cart is empty — the gallery wall is waiting.</p>
          <Link page="gallery" go={go} className="kg-btn kg-btn-primary">Browse paintings</Link></div>
      ) : (
        <div>
          <ul className="kg-lines">
            {lines.map((l) => (
              <li key={l.token} className="kg-line">
                <a className="kg-line-img" href={l.href} aria-label={`View ${l.title}`}
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                    e.preventDefault();
                    go(l.kind === "shop" ? "shopitem" : "painting", l.id);
                  }}>
                  <img src={l.image} alt="" />
                </a>
                <div className="kg-line-info">
                  <div className="kg-display kg-line-title">{l.title}</div>
                  <div className="kg-muted kg-small">{l.sub}</div>
                  {/* Only reproductions can be bought more than once, so only they
                      get a counter. An original has nothing to count. */}
                  {l.kind === "shop" && (
                    <div className="kg-qty">
                      <button className="kg-qty-btn" aria-label={`One fewer ${l.title}`}
                        onClick={() => removeLine(l.token)}><Ic d={P.minus} size={13} /></button>
                      <span aria-live="polite">{l.qty}</span>
                      <button className="kg-qty-btn" aria-label={`One more ${l.title}`}
                        onClick={() => addLine(l.token)}><Ic d={P.plus} size={13} /></button>
                    </div>
                  )}
                </div>
                <div className="kg-line-price">{money(l.price * l.qty)}</div>
                <button className="kg-iconbtn" aria-label={`Remove ${l.title} from cart`}
                  onClick={() => removeLine(l.token, true)}><Ic d={P.x} size={16} /></button>
              </li>
            ))}
          </ul>
          <div className="kg-total"><span>Total</span><span>{money(total)}</span></div>
          {/* Whatever currency someone is browsing in, the charge is Canadian
              dollars — so say the Canadian figure before they go any further. */}
          {approx && <p className="kg-muted kg-small kg-fxnote">Approximate. You'll be charged {base(total)}.</p>}
          <p className="kg-muted kg-small">
            {lines.some((l) => l.kind === "painting")
              ? "Each painting is a one-of-a-kind original — it's reserved the moment your order is confirmed."
              : "Shipping is included in the price."}
          </p>
          <button className="kg-btn kg-btn-primary kg-btn-lg" onClick={() => go("checkout")}>Continue to checkout <Ic d={P.arrow} size={16} /></button>
        </div>
      )}
    </div>
  );
}

export function Checkout({ lines, total, orderLines, placeOrder, payByCard, cardEnabled, go }) {
  /* Deliberately the base formatter, not the browsing one: this is the page
     where a number becomes a commitment, so every figure on it is the Canadian
     amount actually being charged, with the visitor's own currency alongside as
     a note rather than as the headline. */
  const { base, money, approx } = useCurrency();
  const items = lines;

  /* Asking someone to email for payment details makes sense for a one-of-a-kind
     original — they may want to talk about framing, or to know who they are
     buying from first. On a $30 print it does the opposite: it invites a wait
     where a card would have done, and its presence quietly suggests the card
     might not work. So it is offered only when an original is in the basket —
     or when cards are off altogether, where it is the only way through. */
  const hasOriginal = items.some((l) => l.kind === "painting");
  const offerInquiry = hasOriginal || !cardEnabled;

  const [mode, setMode] = useState(cardEnabled ? "card" : "inquiry");
  useEffect(() => { if (cardEnabled) setMode("card"); }, [cardEnabled]);
  /* If the only reason for inquiry mode disappears — the original was removed —
     don't strand someone on a choice that is no longer on screen. */
  useEffect(() => { if (!offerInquiry && mode === "inquiry") setMode("card"); }, [offerInquiry, mode]);

  const [f, setF] = useState({ name: "", email: "", address: "", note: "" });
  const [busy, setBusy] = useState(false);
  /* Card buyers give their name and email to Stripe a moment later, so the
     fields here are genuinely optional — and two boxes labelled "optional" are
     just work with no purpose. They stay behind a link for the few who want to
     send a note, and open on their own if anything has been typed. */
  const [showDetails, setShowDetails] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const card = mode === "card";
  const detailsOpen = !card || showDetails || f.name || f.email || f.note || f.address;
  if (items.length === 0) return (
    <div className="kg-page kg-wrap kg-section"><p className="kg-muted">Your cart is empty.</p>
      <Link page="gallery" go={go} className="kg-btn kg-btn-primary">Back to the gallery</Link></div>
  );
  const opt = (v, t, d) => (
    <label key={v} className={"kg-option" + (mode === v ? " on" : "")}>
      <input type="radio" name="mode" value={v} checked={mode === v} onChange={() => setMode(v)} />
      <span><strong>{t}</strong><span className="kg-small kg-muted">{d}</span></span>
    </label>
  );
  return (
    <div className="kg-page kg-wrap kg-section">
      <header className="kg-pagehead">
        <div className="kg-caps">Almost home</div>
        <h1 className="kg-display kg-h1">Checkout</h1>
      </header>
      {/* With no payment choice to make and the optional fields closed, the left
          column holds a single link — a two-column grid then reads as a page
          that failed to load. In that case the summary stands on its own. */}
      <div className={"kg-checkout" + (!offerInquiry && !detailsOpen ? " lean" : "")}>
        <div>
          {/* With only one way to pay there is no choice to present, so the
              radio buttons are skipped rather than shown with a single option. */}
          {offerInquiry && (
            <div className="kg-field"><label>How would you like to complete this?</label>
              <div className="kg-options">
                {cardEnabled && opt("card", "Pay by card", "You'll be taken to Stripe's secure payment page, then straight back here.")}
                {opt("inquiry", "Purchase inquiry", "Send your order to Karima directly — she'll reply by email with payment and shipping details.")}
              </div>
            </div>
          )}

          {!detailsOpen ? (
            <button type="button" className="kg-textlink kg-detailstoggle" onClick={() => setShowDetails(true)}>
              Add your details or a note <span className="kg-opt">(optional)</span>
            </button>
          ) : (
            <>
              <div className="kg-field"><label htmlFor="o-name">Full name{card && <span className="kg-opt"> (optional — Stripe will ask)</span>}</label>
                <input id="o-name" className="kg-input" value={f.name} onChange={set("name")} autoComplete="name" /></div>
              <div className="kg-field"><label htmlFor="o-email">Email{card && <span className="kg-opt"> (optional — Stripe will ask)</span>}</label>
                <input id="o-email" className="kg-input" type="email" value={f.email} onChange={set("email")} autoComplete="email" /></div>
              {!card && (
                <div className="kg-field"><label htmlFor="o-addr">Shipping address <span className="kg-opt">(optional)</span></label>
                  <textarea id="o-addr" className="kg-textarea" rows={2} value={f.address} onChange={set("address")} autoComplete="street-address" /></div>
              )}
              <div className="kg-field"><label htmlFor="o-note">Note to the artist</label>
                <textarea id="o-note" className="kg-textarea" rows={2} value={f.note} onChange={set("note")}
                  placeholder={hasOriginal ? "Anything I should know — framing, timing, a story…" : "Anything I should know — timing, a gift message…"} /></div>
            </>
          )}
        </div>

        <aside className="kg-summary" aria-label="Order summary">
          <div className="kg-caps">Order summary</div>
          <ul className="kg-sumlist">
            {items.map((l) => (
              <li key={l.token}>
                <img src={l.image} alt="" />
                {/* The size belongs here above all: this is the last screen
                    before paying, and it was the one screen that didn't say
                    which size had been chosen. */}
                <span className="kg-sumline">
                  <span className="kg-display">{l.title}{l.qty > 1 ? ` × ${l.qty}` : ""}</span>
                  {l.sub && <span className="kg-sumsub">{l.sub}</span>}
                </span>
                <span>{base(l.price * l.qty)}</span>
              </li>
            ))}
          </ul>
          <div className="kg-total"><span>Total (CAD)</span><span>{base(total)}</span></div>
          {approx && (
            <p className="kg-small kg-muted kg-fxnote">
              About {money(total)} in your currency. Stripe shows the final amount in your
              own money on the payment page, at its rate on the day.
            </p>
          )}
          <button className="kg-btn kg-btn-primary kg-btn-lg kg-block" disabled={busy || (!card && (!f.name.trim() || !f.email.trim()))}
            onClick={async () => {
              if (card) { setBusy(true); await payByCard(f); setBusy(false); }
              else placeOrder({ ...f, mode: "inquiry", items: orderLines, total });
            }}>
            {card ? (busy ? "Taking you to Stripe…" : `Pay ${base(total)} by card`) : "Send purchase inquiry"}
          </button>
          {/* Silence about delivery reads as the bad answer, so it is stated
              before the button rather than left to an email afterwards. */}
          <p className="kg-small kg-muted kg-delivery">
            Shipping is included. Delivery takes 1–2 weeks within Canada and
            3–4 weeks elsewhere.
          </p>
          <p className="kg-small kg-muted">
            {card
              ? "Payment is handled entirely by Stripe — your card details are never sent to this website or stored by it."
              : "No payment is taken now. Karima will write to you with payment and shipping details."}
          </p>
        </aside>
      </div>
    </div>
  );
}

/* ============================ CONFIRM ============================ */
export function Confirm({ order, go }) {
  /* A placed order is a record of what was charged. That was Canadian dollars,
     so this page says so regardless of what the visitor is browsing in. */
  const { base } = useCurrency();
  if (!order) return null;
  return (
    <div className="kg-page kg-wrap kg-section kg-narrow kg-center">
      <div className="kg-bigcheck"><Ic d={P.check} size={34} sw={2} /></div>
      <h1 className="kg-display kg-h1">{order.mode === "card" ? "Payment received" : "Inquiry sent"}</h1>
      <div className="kg-caps">Reference {order.ref}</div>
      <p className="kg-lede">
        {order.mode === "card"
          ? "Thank you — your payment went through and a confirmation is on its way to your inbox. Karima packs and ships each original herself, and will be in touch with tracking details."
          : `Thank you, ${(order.name || "").split(" ")[0] || "friend"}. Your inquiry is in Karima's studio inbox — she'll write to you at ${order.email} with payment and shipping details.`}
      </p>
      {(order.items || []).length > 0 && (
        <div className="kg-summary kg-left">
          <ul className="kg-sumlist">
            {(order.items || []).map((i) => (
              <li key={i.id}><span className="kg-display">{i.title}</span><span>{base(i.price)}</span></li>
            ))}
          </ul>
          <div className="kg-total"><span>Total</span><span>{base(order.total)}</span></div>
        </div>
      )}
      <Link page="gallery" go={go} className="kg-btn kg-btn-primary">Back to the gallery</Link>
    </div>
  );
}
